import { isWeekVisibleForAthlete, toISODate } from "./programVisibility";
import { normalizeProgramColor } from "./programColors";

/**
 * Il blocco «Oggi» della dashboard coach: quali programmi hanno qualcosa da
 * fare oggi per gli atleti, letto sull'elenco compatto (`/programs?compact=true`).
 *
 * "Oggi" e' quello che vede l'atleta: una giornata datata oggi in una settimana
 * consegnata (pubblicata o con `publish_date` raggiunta, non disattivata). Le
 * settimane in anteprima per singoli atleti non contano: il blocco parla del
 * programma, non di una persona. Spec: docs/coach-today/01-analisi.md.
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

/** Un programma arriva a qualcuno: atleti diretti, assegnazioni o gruppi. */
export const hasProgramRecipients = (program) =>
	asArray(program?.athlete_ids).length > 0 ||
	asArray(program?.athlete_assignments).length > 0 ||
	asArray(program?.group_ids).length > 0;

/** Le giornate datate `todayIso` che l'atleta vede, con la settimana che le contiene. */
export const getDeliveredDaysOn = (program, todayIso) => {
	const out = [];
	asArray(program?.weeks).forEach((week, weekIndex) => {
		if (!isWeekVisibleForAthlete(program, week, todayIso)) return;
		asArray(week?.days).forEach((day) => {
			if (day?.date === todayIso) out.push({ day, week, weekIndex });
		});
	});
	return out;
};

/** Le settimane consegnate di un programma senza date. */
const hasVisibleUndatedWeek = (program, todayIso) =>
	asArray(program?.weeks).some((week) =>
		isWeekVisibleForAthlete(program, week, todayIso),
	);

/** Titolo della giornata, poi le etichette delle sedute; vuoto se nessuno dei due. */
export const describeDay = (day) => {
	const title = String(day?.title || "").trim();
	if (title) return title;
	return asArray(day?.sessions)
		.map((session) => String(session?.label || "").trim())
		.filter(Boolean)
		.join(" + ");
};

/**
 * Chi riceve il programma: i nomi dei gruppi che conosco e il numero di atleti
 * (diretti + membri dei gruppi, senza doppioni). I gruppi fuori dal mio elenco
 * si contano come gruppi, non come atleti: non so chi c'e' dentro.
 */
export const describeRecipients = (program, groupsById = {}) => {
	const athleteIds = new Set([
		...asArray(program?.athlete_ids),
		...asArray(program?.athlete_assignments)
			.map((assignment) => assignment?.athlete_id)
			.filter(Boolean),
	]);
	const groupNames = [];
	let unknownGroups = 0;
	asArray(program?.group_ids).forEach((groupId) => {
		const group = groupsById[groupId];
		if (!group) {
			unknownGroups += 1;
			return;
		}
		if (group.name) groupNames.push(group.name);
		asArray(group.athlete_ids).forEach((id) => athleteIds.add(id));
	});
	return { groupNames, athleteCount: athleteIds.size, unknownGroups };
};

/**
 * Le righe del blocco «Oggi», gia' ordinate: prima le giornate datate oggi
 * (programmi miei, poi condivisi), poi i programmi senza date ancora in corso.
 * Un programma compare una volta sola anche se arriva sia come mio sia come
 * condiviso.
 */
export const buildTodayProgramEntries = (
	programs,
	{ todayIso = toISODate(new Date()), userId = null, groupsById = {} } = {},
) => {
	const seen = new Set();
	const dated = [];
	const undated = [];

	asArray(programs).forEach((program, index) => {
		const programId = program?.program_id;
		if (!programId || seen.has(programId)) return;
		if (program?.is_deleted) return;
		if (!hasProgramRecipients(program)) return;

		const base = {
			programId,
			title: program.title || program.name || "",
			// L'etichetta colorata del coach, per ritrovare la riga a colpo d'occhio.
			color: normalizeProgramColor(program.color),
			isShared: Boolean(userId) && program.coach_id !== userId,
			isDated: program.is_dated !== false,
			sortOrder: Number.isFinite(program.sort_order) ? program.sort_order : index,
			recipients: describeRecipients(program, groupsById),
			program,
		};

		if (program.is_dated === false) {
			if (!hasVisibleUndatedWeek(program, todayIso)) return;
			seen.add(programId);
			undated.push({ ...base, dayId: null, weekIndex: 0, dayLabel: "", sessionCount: 0 });
			return;
		}

		const [delivered] = getDeliveredDaysOn(program, todayIso);
		if (!delivered) return;
		seen.add(programId);
		dated.push({
			...base,
			dayId: delivered.day?.day_id || null,
			weekIndex: delivered.weekIndex,
			dayLabel: describeDay(delivered.day),
			sessionCount: asArray(delivered.day?.sessions).length,
		});
	});

	const byOwnerThenOrder = (a, b) => {
		if (a.isShared !== b.isShared) return a.isShared ? 1 : -1;
		return a.sortOrder - b.sortOrder;
	};
	return [...dated.sort(byOwnerThenOrder), ...undated.sort(byOwnerThenOrder)];
};

/**
 * Le settimane che l'atleta vede di un programma intero, pronte per
 * l'anteprima. Le settimane ridotte a scheletro da `weeks_window` non hanno
 * giornate: mostrarle darebbe una settimana vuota, quindi si tolgono.
 */
export const buildProgramPreview = (program, todayIso = toISODate(new Date())) => {
	if (!program) return null;
	const weeks = asArray(program.weeks).filter(
		(week) =>
			week?.days_omitted !== true &&
			isWeekVisibleForAthlete(program, week, todayIso),
	);
	return { ...program, weeks };
};

/**
 * `weeks_window` taglia le settimane vecchie: va bene solo per un programma
 * datato le cui settimane passate l'atleta comunque non vede. Per gli altri
 * (senza date, settimane che non scadono, o una settimana che il coach ha
 * tenuto visibile: riattivata a mano o con una data di disattivazione sua)
 * serve il programma intero.
 */
export const programPreviewPath = (program) => {
	const id = encodeURIComponent(program?.program_id || "");
	const keepsPastWeeks = (program?.weeks || []).some(
		(week) => week?.keep_visible === true || Boolean(week?.disable_date),
	);
	const windowed =
		program?.is_dated !== false &&
		program?.auto_disable_weeks !== false &&
		!keepsPastWeeks;
	return windowed ? `/programs/${id}?weeks_window=1` : `/programs/${id}`;
};
